import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import dig from 'object-dig';

import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Link from '@material-ui/core/Link';
import Chip from '@material-ui/core/Chip';
import FaceIcon from '@material-ui/icons/Face';
import DeleteIcon from '@material-ui/icons/Delete';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import BusinessIcon from '@material-ui/icons/Business';
import EditIcon from '@material-ui/icons/Edit';

import Table from '~/components/Table/Table';
import ModalDelete from '~/components/ModalDelete';
import IconButton from '~/components/Button/IconButton';
import ChipItem from '~/components/Chip';

import Request from '~/providers/Request';
import isPresent from '~/utils/isPresent';
import { packagesColors, packageOptions } from '~/config/constants';
import { showCorporation, showPerson } from '~/config/routes';
import Can from '~/contexts/Can';

import AddPackageForm from './AddPackageForm';
import EditPackageModal from './EditPackageModal';

function PackageList({
  user,
  owner,
  onUpdateAddons,
  isAddPackage,
  setIsAddPackage,
}) {
  const { t } = useTranslation();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [packageRemove, setPackageRemove] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);

  const [loadingEdit, setLoadingEdit] = useState(false);
  const [actualPackage, setActualPackage] = useState(null);
  const [cancelDate, setCancelDate] = useState(null);

  const onUpdatePackages = (newPackages) => {
    setPackages(newPackages.data);
    if (typeof onUpdateAddons === 'function') {
      onUpdateAddons(newPackages);
    }
  };

  function getPackages() {
    setLoading(true);
    Request.get(`/admin/users/${user.id}/packages`, null, {
      normalizeJSON: false,
    })
      .then((response) => {
        setPackages(response.data);
        setLoading(false);
      })
      .catch((e) => {
        console.error(e);
        setLoading(false);
      });
  }

  const handleCloseEditModal = () => {
    setActualPackage(null);
    setCancelDate(null);

    setOpenEditModal(false);
  };

  const handleEditCancelDate = () => {
    setLoadingEdit(true);

    if (!moment(cancelDate).isValid()) {
      setLoadingEdit(false);
      toast.error('Digite uma data válida!');
      return;
    }

    Request.put(
      `/admin/users/${actualPackage.owner_id}/packages/${actualPackage.id}`,
      {
        cancel_date: cancelDate,
      },
      null,
      {
        normalizeJSON: false,
      }
    )
      .then(() => {
        handleCloseEditModal();
        setActualPackage(null);
        setLoadingEdit(false);
        getPackages();
        toast.success('Pacote atualizado com sucesso!');
      })
      .catch((e) => {
        console.error(e);
        setLoadingEdit(false);
      });
  };

  function deletePackage() {
    setLoadingDelete(true);
    Request.del(`/admin/users/${user.id}/packages/${packageRemove.id}`, null, {
      normalizeJSON: false,
    })
      .then((response) => {
        setLoadingDelete(false);
        setPackageRemove(null);
        onUpdatePackages(response);
        toast.success(t('delete-toast-success'));
      })
      .catch((e) => {
        console.error(e);
        setLoadingDelete(false);
      });
  }

  useEffect(() => {
    getPackages();
  }, [user]);

  useEffect(() => {
    if (actualPackage) {
      setCancelDate(moment(actualPackage.cancel_date).format());
    } else {
      setCancelDate(null);
    }
  }, [actualPackage]);

  return (
    <>
      <Table
        emptyData={packages.length === 0}
        loading={loading}
        columns={
          <>
            <TableCell>{t('action.actions')}</TableCell>
            <TableCell>{t('person.attributes.pack')}</TableCell>
            <TableCell>{t('person.attributes.specialty')}</TableCell>
            <TableCell>{t('person.attributes.packPackage')}</TableCell>
            <TableCell>{t('person.attributes.enabledBy')}</TableCell>
            <TableCell>{t('person.attributes.partner')}</TableCell>
            <TableCell>{t('person.attributes.enabledIn')}</TableCell>
            <TableCell>{t('person.attributes.canceledDate')}</TableCell>
          </>
        }
      >
        {packages.map((row) => {
          return (
            <TableRow key={row.id} hover>
              <TableCell
                style={{
                  display: 'flex',
                  justifyContent: 'space-around',
                  flexDirection: 'row',
                }}
              >
                <Can I="edit_package" a="Peoples">
                  <IconButton
                    tooltip={t('action.edit')}
                    onClick={() => {
                      setActualPackage(row);
                      setOpenEditModal(true);
                    }}
                    Icon={EditIcon}
                    iconColor="orange"
                  />
                </Can>
                <Can I="delete_package" a="Peoples">
                  <IconButton
                    tooltip={t('action.delete')}
                    onClick={() => {
                      setPackageRemove(row);
                    }}
                    Icon={DeleteIcon}
                    iconColor="red"
                  />
                </Can>
              </TableCell>
              <TableCell>
                <ChipItem
                  label={packageOptions[row.pack_type]}
                  colorHex={packagesColors[row.pack_type]}
                />
              </TableCell>
              <TableCell>{dig(row, 'specialty_name')}</TableCell>
              <TableCell>{dig(row, 'pack_name')}</TableCell>
              <TableCell>
                {row.owner && (
                  <Chip
                    icon={<FaceIcon />}
                    label={row.owner.name}
                    component={Link}
                    href={showPerson.reverse({ id: row.owner.id })}
                    style={{ cursor: 'pointer' }}
                    target="_blank"
                    color="primary"
                    variant="outlined"
                    onDelete={() => {}}
                    deleteIcon={<OpenInNewIcon />}
                  />
                )}
              </TableCell>
              <TableCell>
                {row.partner && (
                  <Chip
                    icon={<BusinessIcon />}
                    label={row.partner.name}
                    component={Link}
                    href={showCorporation.reverse({
                      id: row.partner.corporation_id,
                    })}
                    style={{ cursor: 'pointer' }}
                    target="_blank"
                    color="primary"
                    variant="outlined"
                    onDelete={() => {}}
                    deleteIcon={<OpenInNewIcon />}
                  />
                )}
              </TableCell>
              <TableCell>
                {moment(row.created_at).format('DD/MM/YYYY HH:mm')}
              </TableCell>
              <TableCell>
                {moment(row.cancel_date).isValid() &&
                  moment(row.cancel_date).format('DD/MM/YYYY HH:mm')}
              </TableCell>
            </TableRow>
          );
        })}
      </Table>
      <ModalDelete
        modal={isPresent(packageRemove)}
        onClose={() => {
          setPackageRemove(null);
        }}
        isLoading={loadingDelete}
        deleteAction={() => deletePackage()}
      />
      <AddPackageForm
        setPackages={onUpdatePackages}
        isOpen={isAddPackage}
        user={user}
        owner={owner}
        setIsOpen={setIsAddPackage}
      />
      <EditPackageModal
        isOpen={openEditModal}
        actualPackage={actualPackage}
        handleSave={handleEditCancelDate}
        handleClose={handleCloseEditModal}
        cancelDate={cancelDate}
        setCancelDate={setCancelDate}
        isLoading={loadingEdit}
      />
    </>
  );
}

PackageList.propTypes = {
  isAddPackage: PropTypes.bool.isRequired,
  setIsAddPackage: PropTypes.func.isRequired,
  onUpdateAddons: PropTypes.func,
  user: PropTypes.object.isRequired,
  owner: PropTypes.object.isRequired,
};

export default React.memo(PackageList);
